import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Compass,
  Clapperboard,
  MessageSquare,
  User,
  Users,
  Plus,
  Briefcase,
  Grid,
  FileText,
  Settings,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Badge } from './ui/badge';
import { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = window.__BACKEND_URL__;

const NAV_THEME = {
  active: 'var(--mobile-nav-active, var(--theme-primary, #6D28D9))',
  inactive: 'var(--mobile-nav-inactive, var(--theme-text-muted, #6B7280))',
  bg: 'var(--mobile-nav-bg, var(--theme-surface, #FFFFFF))',
  border: 'var(--mobile-nav-border, var(--theme-border, rgba(15, 23, 42, 0.12)))',
  badgeBg: 'var(--mobile-nav-badge-bg, var(--theme-primary, #6D28D9))',
  badgeText: 'var(--mobile-nav-badge-text, #FFFFFF)',
};

// Bottom tabbar role-specific entries. Paths intentionally include the
// `?tab=` query string used by `DashboardPage` so each tab actually opens
// the correct view (previously all admin tabs pointed at `/dashboard`
// without a query and therefore all opened Overview — see launch audit).
//
// This file deliberately stays a small, hardcoded bottom-nav for now to
// keep the bottom rail lightweight. The full mobile drawer continues to
// be driven by `config/navigation.js` (`getMobileDrawer()`) and is
// untouched.
const TALENT_TABS = [
  { label: 'Home', icon: Home, path: '/dashboard', position: 1 },
  { label: 'Discover', icon: Compass, path: '/casting-calls', position: 2 },
  { label: 'Apply', icon: Clapperboard, path: '/casting-calls', position: 3, isPrimary: true },
  { label: 'Messages', icon: MessageSquare, path: '/messages', position: 4, showBadge: true },
  { label: 'Profile', icon: User, path: '/dashboard?tab=profile', position: 5 },
];

const CASTING_TABS = [
  { label: 'Home', icon: Home, path: '/dashboard', position: 1 },
  { label: 'Applicants', icon: Users, path: '/dashboard?tab=applicants', position: 2 },
  { label: 'Post', icon: Plus, path: '/casting-calls/new', position: 3, isPrimary: true },
  { label: 'Messages', icon: MessageSquare, path: '/messages', position: 4, showBadge: true },
  { label: 'Account', icon: Briefcase, path: '/dashboard?tab=settings', position: 5 },
];

const ADMIN_TABS = [
  { label: 'Overview', icon: Grid, path: '/dashboard', position: 1 },
  { label: 'Users', icon: Users, path: '/dashboard?tab=users', position: 2 },
  { label: 'Content', icon: FileText, path: '/dashboard?tab=content', position: 3 },
  { label: 'Settings', icon: Settings, path: '/dashboard?tab=settings', position: 4 },
];

const FALLBACK_TABS = [
  { label: 'Home', icon: Home, path: '/dashboard', position: 1 },
  { label: 'Discover', icon: Compass, path: '/casting-calls', position: 2 },
  { label: 'Messages', icon: MessageSquare, path: '/messages', position: 3, showBadge: true },
  { label: 'Profile', icon: User, path: '/dashboard?tab=profile', position: 4 },
];

const getTabsForRole = (role) => {
  if (role === 'talent') return TALENT_TABS;

  if (role === 'casting_professional' || role === 'production_company') {
    return CASTING_TABS;
  }

  if (role === 'admin') return ADMIN_TABS;

  return FALLBACK_TABS;
};

const MobileNav = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch message stats
  useEffect(() => {
    let alive = true;

    const fetchStats = async () => {
      if (!user) return;

      try {
        const { data } = await axios.get(`${API_URL}/api/messages/stats`, {
          withCredentials: true,
        });

        if (!alive) return;
        setUnreadCount(data.total_badge_count || 0);
      } catch (error) {
        // Non-fatal. Mobile nav should never break because stats fail.
        console.error('Error fetching message stats:', error);
      }
    };

    fetchStats();

    const interval = setInterval(fetchStats, 30000);

    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [user]);

  // Don't show nav if not logged in.
  // Keep this after hooks so React hook order stays stable.
  if (!user) return null;

  const tabs = getTabsForRole(user.role);

  // Check if current path matches tab destination
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';

    if (path.includes('?')) {
      const [basePath, query] = path.split('?');
      const queryValue = query?.split('=')?.[1];
      return location.pathname === basePath && location.search.includes(queryValue);
    }

    if (path === '/dashboard') {
      // Overview is only active when on /dashboard with no `?tab=` query
      // (or explicit `?tab=overview`). Otherwise another bottom-tab owns
      // the active state. See launch audit for the previous regression
      // where every admin tab pointed at /dashboard and Overview was
      // always highlighted.
      if (location.pathname !== '/dashboard') return false;
      const params = new URLSearchParams(location.search);
      const tab = params.get('tab');
      return !tab || tab === 'overview';
    }

    return location.pathname.startsWith(path);
  };

  return (
    <nav
      data-mobile-nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-50 border-t transition-colors duration-300"
      style={{
        background:
          'linear-gradient(180deg, color-mix(in srgb, var(--mobile-nav-bg, var(--theme-surface, #FFFFFF)) 94%, transparent) 0%, var(--mobile-nav-bg, var(--theme-surface, #FFFFFF)) 100%)',
        backgroundColor: NAV_THEME.bg,
        borderTopColor: NAV_THEME.border,
        paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)',
        boxShadow:
          '0 -18px 45px rgba(15, 23, 42, 0.10), 0 -1px 0 color-mix(in srgb, var(--theme-border, rgba(15,23,42,0.12)) 70%, transparent)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
      }}
    >
      <div className="relative">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px"
          style={{
            background:
              'linear-gradient(90deg, transparent, color-mix(in srgb, var(--theme-primary, #6D28D9) 45%, transparent), transparent)',
          }}
        />

        <div className="flex items-end justify-around gap-1 px-2 pt-2 pb-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = isActive(tab.path);
            const isPrimary = tab.isPrimary;

            return (
              <Link
                key={tab.position}
                to={tab.path}
                className={`group flex flex-col items-center justify-center relative rounded-2xl transition-all duration-200 active:scale-95 ${
                  isPrimary ? '-mt-5 px-1' : 'px-1'
                }`}
                aria-label={tab.label}
                data-testid={`mobile-nav-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {isPrimary ? (
                  <div className="flex flex-col items-center">
                    <div
                      className="relative rounded-full p-3.5 shadow-xl mb-1 transition-all duration-300 group-hover:scale-105"
                      style={{
                        background:
                          'linear-gradient(135deg, var(--mobile-nav-raised-bg, var(--theme-primary, #6D28D9)) 0%, var(--theme-primary-hover, #0055FF) 100%)',
                        boxShadow:
                          '0 14px 30px color-mix(in srgb, var(--theme-primary, #6D28D9) 30%, transparent), inset 0 1px 0 rgba(255,255,255,0.30)',
                      }}
                    >
                      <Icon
                        className="h-6 w-6 transition-colors duration-300"
                        strokeWidth={2.25}
                        style={{
                          color: 'var(--mobile-nav-raised-text, #FFFFFF)',
                        }}
                      />
                    </div>

                    <span
                      className="text-[11px] font-bold leading-tight transition-colors duration-300"
                      style={{
                        color: NAV_THEME.active,
                      }}
                    >
                      {tab.label}
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-1 px-1.5 min-w-[58px]">
                    <div
                      className="relative h-8 w-8 rounded-2xl flex items-center justify-center transition-all duration-300"
                      style={{
                        backgroundColor: active
                          ? 'color-mix(in srgb, var(--theme-primary, #6D28D9) 12%, transparent)'
                          : 'transparent',
                      }}
                    >
                      <Icon
                        className="h-5 w-5 transition-all duration-300"
                        strokeWidth={active ? 2.55 : 2.1}
                        fill={active ? NAV_THEME.active : 'none'}
                        style={{
                          color: active ? NAV_THEME.active : NAV_THEME.inactive,
                          filter: active
                            ? 'drop-shadow(0 6px 10px color-mix(in srgb, var(--theme-primary, #6D28D9) 22%, transparent))'
                            : 'none',
                        }}
                      />

                      {tab.showBadge && unreadCount > 0 && (
                        <Badge
                          className="absolute -top-1.5 -right-1.5 h-4 min-w-[16px] px-1 text-[10px] flex items-center justify-center rounded-full border-2 transition-colors duration-300"
                          style={{
                            backgroundColor: NAV_THEME.badgeBg,
                            color: NAV_THEME.badgeText,
                            borderColor: NAV_THEME.bg,
                          }}
                        >
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </Badge>
                      )}
                    </div>

                    <span
                      className="text-[11px] font-semibold mt-0.5 leading-tight transition-colors duration-300"
                      style={{
                        color: active ? NAV_THEME.active : NAV_THEME.inactive,
                      }}
                    >
                      {tab.label}
                    </span>

                    <span
                      className="mt-1 h-0.5 rounded-full transition-all duration-300"
                      style={{
                        width: active ? '18px' : '0px',
                        background:
                          'linear-gradient(90deg, var(--theme-primary, #6D28D9), var(--theme-primary-hover, #0055FF))',
                      }}
                    />
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default MobileNav;
